<template>

  <v-container fluid class="route-tracking-history" v-if="route">
    <!-- Title: Tracking History -->
    <v-row class="pa-0 ma-0">
      <v-col cols="12" class="pt-2">
        <span class="title">Tracking History</span>
      </v-col>
    </v-row>

    <!------------------------------>
    <!-- Delivery Status Workflow -->
    <!------------------------------>
    <v-row class="align-center pa-0 ma-0">
      <v-col xs="12">
        <!------------------->
        <!-- Route Started -->
        <!------------------->
        <div class="pt-3">
          <div class="text-subtitle-1 font-weight-black">
            <v-icon class="mr-2">{{ getWorkflowIcon('route_started') }}</v-icon>
            Route started
          </div>
          <div class="border-left">
            <div class="ml-4 pt-3" v-if="route.on_delivery_at">
              <span class="detail-label">started:</span>
              {{ formatDateAndTime(route.on_delivery_at) }}
            </div>
          </div>
        </div>

        <!--------------------->
        <!-- Vehicle Claimed -->
        <!--------------------->
        <div>
          <div class="text-subtitle-1 font-weight-black">
            <v-icon class="mr-2">{{ getWorkflowIcon('vehicle_claimed') }}</v-icon>
            Vehicle claimed
          </div>
          <div class="border-left">
            <div class="ml-4 pt-3" v-if="route.vehicle_claimed_at">
              <span class="detail-label">claimed:</span>
              {{ formatTime(route.vehicle_claimed_at) }}
            </div>
            <div class="ml-4 mt-3 pb-4" v-if="route.odometer_reading_start">
              <span class="detail-label">odometer reading:</span>
              {{ route.odometer_reading_start }}
            </div>
          </div>
        </div>

        <!----------------->
        <!-- Order Steps -->
        <!----------------->
        <div v-for="(step, stepIndex) in deliverySteps" :key="`step-${stepIndex}`">
          <!-- (pickups) -->
          <template v-if="step.type === 'pickup'">
            <!-- (To Shipper) -->
            <div :key="`to-shipper-${stepIndex}`">
              <div class="text-subtitle-1 font-weight-black">
                <v-icon class="mr-2">{{ getWorkflowIcon('to_shipper', step.order_numbers) }}</v-icon>
                To Shipper -
                <span v-if="step.order_numbers.length > 1">Orders: </span>
                <span v-else>Order: </span>
                {{ step.order_numbers.map(num => ` ${num}`).toString() }}
              </div>
              <div class="border-left">
                <!-- Time and Destination -->
                <template v-if="getOrderValueFromArray('to_shipper_at', step.order_numbers)">
                  <div class="ml-4 pt-3">
                    <span class="detail-label">to shipper:</span>
                    {{ formatTime(getOrderValueFromArray('to_shipper_at', step.order_numbers)) }}
                  </div>
                  <div class="ml-4 mt-3 pb-4">
                    <span class="detail-label">destination:</span>
                    {{ getOrderValueFromArray('shipper.name', step.order_numbers) }}
                  </div>
                </template>
              </div>
            </div>
            <!-- (At Shipper) -->
            <div :key="`at-shipper-${stepIndex}`">
              <div class="text-subtitle-1 font-weight-black">
                <v-icon class="mr-2">{{ getWorkflowIcon('at_shipper', step.order_numbers) }}</v-icon>
                At Shipper -
                <span v-if="step.order_numbers.length > 1">Orders: </span>
                <span v-else>Order: </span>
                {{ step.order_numbers.map(num => ` ${num}`).toString() }}
              </div>
              <div class="border-left">
                <!-- Location and Time -->
                <template v-if="getOrderValueFromArray('at_shipper_at', step.order_numbers)">
                  <div class="ml-4 pt-3">
                    <span class="detail-label">location:</span>
                    {{ getOrderValueFromArray('shipper.name', step.order_numbers) }}
                  </div>
                  <div class="ml-4 mt-3">
                    <span class="detail-label">at shipper:</span>
                    {{ formatTime(getOrderValueFromArray('at_shipper_at', step.order_numbers)) }}
                  </div>
                </template>
                <!-- Orders Picked-up -->
                <template v-for="(orderNum) in step.order_numbers">
                  <div class="ml-4 mt-6" v-if="getOrderValueFromArray('picked_up_at', orderNum)" :key="`num-${orderNum}`">
                    Order <span style="margin-left:4px;font-size:19px;">{{ orderNum }}</span>
                  </div>
                  <div class="ml-4 mt-3" v-if="getOrderValueFromArray('code_confirmed_at', orderNum)" :key="`confirmed-${orderNum}`">
                    <span class="detail-label">confirmed:</span>
                    {{ formatTime(getOrderValueFromArray('code_confirmed_at', orderNum)) }}
                  </div>
                  <div class="ml-4 mt-3" v-if="getOrderValueFromArray('items_checked_at', orderNum)" :key="`items-checked-${orderNum}`">
                    <span class="detail-label">items checked:</span>
                    {{ formatTime(getOrderValueFromArray('items_checked_at', orderNum)) }}
                  </div>
                  <div class="ml-4 mt-3" v-if="getOrderValueFromArray('picked_up_at', orderNum)" :key="`picked-up-${orderNum}`">
                    <span class="detail-label">picked-up:</span>
                    {{ formatTime(getOrderValueFromArray('picked_up_at', orderNum)) }}
                  </div>
                </template>
                <div class="pb-4"></div>
              </div>
            </div>
          </template>

          <!-- (dropoffs) -->
          <template v-if="step.type === 'dropoff'">
            <!-- (To Receiver) -->
            <div :key="`to-receiver-${stepIndex}`">
              <div class="text-subtitle-1 font-weight-black">
                <v-icon class="mr-2">{{ getWorkflowIcon('to_receiver', step.order_numbers) }}</v-icon>
                To Receiver -
                <span v-if="step.order_numbers.length > 1">Orders: </span>
                <span v-else>Order: </span>
                {{ step.order_numbers.map(num => ` ${num}`).toString() }}
              </div>
              <div class="border-left">
                <!-- Time and Destination -->
                <template v-if="getOrderValueFromArray('to_receiver_at', step.order_numbers)">
                  <div class="ml-4 pt-3">
                    <span class="detail-label">to receiver:</span>
                    {{ formatTime(getOrderValueFromArray('to_receiver_at', step.order_numbers)) }}
                  </div>
                  <div class="ml-4 mt-3 pb-4">
                    <span class="detail-label">destination:</span>
                    {{ getOrderValueFromArray('receiver.name', step.order_numbers) }}
                  </div>
                </template>
              </div>
            </div>
            <!-- (At Receiver) -->
            <div :key="`at-receiver-${stepIndex}`">
              <div class="text-subtitle-1 font-weight-black">
                <v-icon class="mr-2">{{ getWorkflowIcon('at_receiver', step.order_numbers) }}</v-icon>
                At Receiver -
                <span v-if="step.order_numbers.length > 1">Orders: </span>
                <span v-else>Order: </span>
                {{ step.order_numbers.map(num => ` ${num}`).toString() }}
              </div>
              <div class="border-left">
                <!-- Location and Time -->
                <template v-if="getOrderValueFromArray('at_receiver_at', step.order_numbers)">
                  <div class="ml-4 pt-3">
                    <span class="detail-label">location:</span>
                    {{ getOrderValueFromArray('receiver.name', step.order_numbers) }}
                  </div>
                  <div class="ml-4 mt-3 pb-4">
                    <span class="detail-label">at receiver:</span>
                    {{ formatTime(getOrderValueFromArray('at_receiver_at', step.order_numbers)) }}
                  </div>
                </template>
              </div>
            </div>
            <!-- (Order Completed) -->
            <div :key="`delivered-${stepIndex}`">
              <div class="text-subtitle-1 font-weight-black">
                <v-icon class="mr-2">{{ getWorkflowIcon('order_delivered', step.order_numbers) }}</v-icon>
                Completed -
                <span v-if="step.order_numbers.length > 1">Orders: </span>
                <span v-else>Order: </span>
                {{ step.order_numbers.map(num => ` ${num}`).toString() }}
              </div>
              <div class="border-left">
                <!-- Orders Delivered -->
                <template v-for="(orderNum) in step.order_numbers">
                  <div class="ml-4 pt-3" v-if="getOrderValueFromArray('receiver_signed_at', orderNum)" :key="`delivered-num-${orderNum}`">
                    Order <span style="margin-left:4px;font-size:19px;">{{ orderNum }}</span>
                  </div>
                  <!-- (rejected order) -->
                  <template v-if="getOrderValueFromArray('rejected_at', orderNum)">
                    <div class="ml-4 mt-3" :key="`rejected-alert-${orderNum}`">
                      <span class="detail-label">order was rejected:</span>
                      {{ formatTime(getOrderValueFromArray('rejected_at', orderNum)) }}
                    </div>
                    <div class="ml-4 mt-3" v-if="getOrderValueFromArray('receiver_notes', orderNum)" :key="`reject-reason-${orderNum}`">
                      <span class="detail-label">reason:</span>
                      {{ getOrderValueFromArray('receiver_notes', orderNum) }}
                    </div>
                    <div class="ml-4 mt-3" v-if="getOrderValueFromArray('receiving_agent_name', orderNum)" :key="`agent-name-${orderNum}`">
                      <span class="detail-label">receiving agent:</span>
                      {{ getOrderValueFromArray('receiving_agent_name', orderNum) }}
                    </div>
                    <div class="ml-4 mt-3" v-if="getOrderValueFromArray('receiver_signed_at', orderNum)" :key="`signed-${orderNum}`">
                      <span class="detail-label">agent signed:</span>
                      {{ formatTime(getOrderValueFromArray('receiver_signed_at', orderNum)) }}
                    </div>
                  </template>
                  <!-- (delivered order) -->
                  <template v-else>
                    <div class="ml-4 mt-3" v-if="getOrderValueFromArray('delivered_at', orderNum)" :key="`delivered-${orderNum}`">
                      <span class="detail-label">order was delivered:</span>
                      {{ formatTime(getOrderValueFromArray('delivered_at', orderNum)) }}
                    </div>
                    <div class="ml-4 mt-3" v-if="getOrderValueFromArray('receiving_agent_name', orderNum)" :key="`agent-name-${orderNum}`">
                      <span class="detail-label">receiving agent:</span>
                      {{ formatAgentName(getOrderValueFromArray('receiving_agent_name', orderNum)) }}
                    </div>
                    <div class="ml-4 mt-3" v-if="getOrderValueFromArray('receiving_agent_fa_license_number', orderNum)" :key="`fa-license-${orderNum}`">
                      <span class="detail-label">FA license:</span>
                      {{ getOrderValueFromArray('receiving_agent_fa_license_number', orderNum) }}
                    </div>
                    <div class="ml-4 mt-3" v-if="getOrderValueFromArray('receiver_signed_at', orderNum)" :key="`signed-${orderNum}`">
                      <span class="detail-label">agent signed:</span>
                      {{ formatTime(getOrderValueFromArray('receiver_signed_at', orderNum)) }}
                    </div>
                  </template>
                </template>
                <div class="pb-4"></div>
              </div>
            </div>
          </template>
        </div>

        <!----------------------->
        <!-- Returning Vehicle -->
        <!----------------------->
        <div>
          <div class="text-subtitle-1 font-weight-black">
            <v-icon class="mr-2">{{ getWorkflowIcon('returning_vehicle') }}</v-icon>
            Returning vehicle
          </div>
          <div class="border-left">
            <div class="ml-4 pt-3" v-if="route.returning_to_hq_at">
              <span class="detail-label">returning:</span>
              {{ formatTime(route.returning_to_hq_at) }}
            </div>
          </div>
        </div>

        <!---------------------->
        <!-- Vehicle Returned -->
        <!---------------------->
        <div>
          <div class="text-subtitle-1 font-weight-black">
            <v-icon class="mr-2">{{ getWorkflowIcon('vehicle_returned') }}</v-icon>
            Vehicle returned
          </div>
          <div class="border-left">
            <div class="ml-4 pt-3" v-if="route.completed_at">
              <span class="detail-label">returned:</span>
              {{ formatTime(route.completed_at) }}
            </div>
            <div class="ml-4 mt-3 pb-4" v-if="route.odometer_reading_end">
              <span class="detail-label">odometer reading:</span>
              {{ route.odometer_reading_end }}
            </div>
          </div>
        </div>

        <!--------------------->
        <!-- Route Completed -->
        <!--------------------->
        <div>
          <div class="text-subtitle-1 font-weight-black">
            <v-icon class="mr-2">{{ getWorkflowIcon('route_completed') }}</v-icon>
            Route completed
          </div>
          <div class="border-left-no-line" v-if="route.completed_at">
            <!-- <div class="ml-6 mt-4">
              delivered: {{ formatDateAndTime(order.delivered_at) }}
            </div>
            <div class="ml-6 mt-3">
              receiving agent: {{ order.receiving_agent_name }}
            </div>
            <div class="ml-6 mt-3 pb-4">
              agent license: {{ order.receiving_agent_fa_license_number }}
            </div> -->
          </div>
        </div>
      </v-col>
    </v-row>

  </v-container>

</template>

<script>

export default {
  name: 'RouteTrackingHistory',

  components: {
  },

  data () {
    return {
      isDataLoading: false,
      activeError: null
    }
  },

  props: {
    route: {
      required: true
    }
  },

  computed: {
    // The orders
    orders () {
      return this.route.orders || []
    },
    // -------------------- Assigned driver
    driver () {
      return (this.route) ? this.route.driver : null
    },
    driverName () {
      if (this.driver) {
        const firstName = this.driver.first_name
        const lastName = this.driver.last_name
        return (lastName) ? `${firstName} ${lastName}` : firstName
      } else {
        return null
      }
    },
    // -------------------- Assigned vehicle
    vehicle () {
      return (this.route) ? this.route.vehicle : null
    },
    // -------------------- Delivery steps (pickups, dropoffs)
    deliverySteps () {
      return (this.route.route_info) ? this.route.route_info.steps : []
    }
  },

  methods: {
    formatTime (date) {
      return (date) ? this.$root.utils.format(date, 'h:mm a z') : ''
    },

    formatDateAndTime (date) {
      return (date) ? this.$root.utils.format(date, 'h:mm a z - MMMM Do, YYYY') : ''
    },

    formatAgentName (name = '') {
      // const words = name.replace(/\s+/g, ' ').trim().split(' ')
      // return (words.length > 1) ? `${words[0]} ${words[1][0]}.` : words[0]
      return name
    },

    // Returns a single order
    getOrder (orderNum) {
      return this.orders.find(order => order.order_number === orderNum)
    },

    getOrderValueFromArray (propPath = '', orderNumbers) {
      const orderNum = (Array.isArray(orderNumbers) && orderNumbers.length > 0) ? orderNumbers[0] : orderNumbers
      const order = this.getOrder(orderNum) || {}
      return this.$root.utils.get(order, propPath)
    },

    getWorkflowIcon (step, orderNumbers = []) {
      const orderNum = (orderNumbers && orderNumbers.length > 0) ? orderNumbers[0] : -1
      const order = this.getOrder(orderNum) || {}

      switch (step) {
        case 'route_started': {
          return (this.route.on_delivery_at) ? 'mdi-checkbox-blank-circle' : 'mdi-checkbox-blank-circle-outline'
        }
        case 'vehicle_claimed': {
          return (this.route.vehicle_claimed_at) ? 'mdi-checkbox-blank-circle' : 'mdi-checkbox-blank-circle-outline'
        }
        case 'to_shipper': {
          return (order.to_shipper_at) ? 'mdi-checkbox-blank-circle' : 'mdi-checkbox-blank-circle-outline'
        }
        case 'at_shipper': {
          return (order.at_shipper_at) ? 'mdi-checkbox-blank-circle' : 'mdi-checkbox-blank-circle-outline'
        }
        case 'to_receiver': {
          return (order.to_receiver_at) ? 'mdi-checkbox-blank-circle' : 'mdi-checkbox-blank-circle-outline'
        }
        case 'at_receiver': {
          return (order.at_receiver_at) ? 'mdi-checkbox-blank-circle' : 'mdi-checkbox-blank-circle-outline'
        }
        case 'order_delivered': {
          return (order.delivered_at || order.rejected_at) ? 'mdi-checkbox-blank-circle' : 'mdi-checkbox-blank-circle-outline'
        }
        case 'returning_vehicle': {
          return (this.route.returning_to_hq_at) ? 'mdi-checkbox-blank-circle' : 'mdi-checkbox-blank-circle-outline'
        }
        case 'vehicle_returned': {
          return (this.route.completed_at) ? 'mdi-checkbox-blank-circle' : 'mdi-checkbox-blank-circle-outline'
        }
        case 'route_completed': {
          return (this.route.completed_at) ? 'mdi-checkbox-blank-circle' : 'mdi-checkbox-blank-circle-outline'
        }
        default: return 'mdi-checkbox-blank-circle-outline'
      }
    }
  }
}
</script>

<style scoped>
  .route-tracking-history .border-left {
    font-size: 14px;
    border-left: 2px solid white;
    margin-left: 11px;
    margin-top: 5px;
    margin-bottom: 5px;
    min-height: 50px;
    align-content: center;
  }

  .route-tracking-history .border-left-no-line {
    font-size: 14px;
    margin-left: 13px;
    margin-top: 5px;
    margin-bottom: 5px;
    align-content: center;
  }

  .route-tracking-history .center {
    padding-top: 3%;
  }

  .route-tracking-history .detail-label {
    color: #9e9e9e;
  }
</style>
